// 웹 푸시 공용 헬퍼 — 전역 자동 재구독 훅(usePushAutoSubscribe)과 안내 배너(PushSetupBanner)가 함께 쓴다.
// 🔴 MyPage.jsx 는 같은 로직을 자체 보유한 채 그대로 둔다(화면 상태 setPushOn 과 얽혀 있어 추출하면
//    폭발 반경이 커진다). /subscribe 는 endpoint 기준 upsert 라 중복 실행돼도 무해하다.
//    단 swReady 하나만 예외 — MyPage 도 여기 것을 쓴다(루트 등록 판정 공유 · #3140).
// 🔴 구독은 루트(/) 등록에만 — ready 가 다른 등록이면 swReady 가 getRegistration('/') 로 루트를 집는다(10-30 #75).
import { pushAPI } from '../api/client';
import { notePushSubscriptionChanged } from '../messengerOnScreen';

// 구독을 바꿨다고 메신저 알림 억제(messengerOnScreen)에 알린다 — 다시 읽게 할 뿐 값은 넘기지 않는다.
// 🔴 시험 대역이 그 export 를 안 내보낼 수 있고(SplitShell.test) 알림 실패가 구독 흐름을 깨면 안 된다 → 확인·삼킨다.
function notePushChanged() {
  try {
    if (typeof notePushSubscriptionChanged !== 'function') return;
    const p = notePushSubscriptionChanged();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch (e) { /* */ }
}

// VAPID 공개키(base64url) → applicationServerKey(Uint8Array)
export function urlB64ToUint8(s) {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function isPushSupported() {
  try {
    return ('serviceWorker' in navigator) && ('PushManager' in window) && ('Notification' in window);
  } catch (e) { return false; }
}

// 게이트웨이 루트(/) 등록인가. scope 가 문자열이 아니면(시험 대역·옛 브라우저) 판정할 값이 없으니 지금처럼 참.
function isRootScope(reg) {
  if (!reg) return false;
  if (typeof reg.scope !== 'string') return true;
  try {
    const u = new URL(reg.scope);
    return u.pathname === '/' && u.origin === window.location.origin;
  } catch (e) { return false; }
}

// ready 가 루트가 아닌 등록(예: /drive 의 Nextcloud SW — push 처리기 없음)이면 루트 등록을 직접 집는다.
// 루트가 없거나·active 가 없거나·예외면 null — 거기 구독하면 푸시마다 크롬 기본 알림이 뜬다(10-30 #75).
function pickRoot(reg) {
  if (!reg || isRootScope(reg)) return reg;
  return Promise.resolve()
    .then(() => navigator.serviceWorker.getRegistration('/'))
    .then((root) => ((isRootScope(root) && root.active) ? root : null))
    .catch(() => null);
}

// SW 미활성 환경(자체서명 인증서 origin·미지원 브라우저)에선 serviceWorker.ready 가 영원히 pending 이다
// → 타임아웃 가드(실패 시 null). 루트 등록 고르기(getRegistration('/'))도 같은 시한 안에서 기다린다 —
//    시한 밖에서 매달리면 로그아웃(unsubscribeCurrentBrowser 3초 상한)과 자동 재구독이 끝나지 않는다.
export function swReady(ms = 6000) {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null);
  return Promise.race([
    Promise.resolve(navigator.serviceWorker.ready).then(pickRoot),
    new Promise((resolve) => { setTimeout(() => resolve(null), ms); }),
  ]);
}

function sameBytes(a, b) {
  if (!a || !b || !a.length || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// 루트(/) 밖 등록에 붙은 «게이트웨이» 구독만 브라우저에서 끊는다(10-30 #75). 끊은 개수를 돌려준다.
// 🔴 applicationServerKey 가 게이트웨이 VAPID 와 바이트로 같은 것만 — 다르거나 비교할 값이 없으면 다른 앱
//    (Nextcloud 등)의 구독일 수 있어 건드리지 않는다. 밖 구독이 하나도 없으면 VAPID 키도 받지 않는다.
// 🔴 서버(pushAPI.unsubscribe)는 부르지 않는다 — notifications_enabled 까지 꺼진다. 서버 행은 다음 발송의 404/410 에서 지워진다.
// 🔴 절대 throw 하지 않는다(실패면 0).
export async function dropForeignGatewaySubscriptions() {
  try {
    const sw = navigator.serviceWorker;
    if (!sw || typeof sw.getRegistrations !== 'function') return 0;
    const regs = await sw.getRegistrations();
    const found = [];
    for (const reg of (regs || [])) {
      if (!reg || typeof reg.scope !== 'string' || isRootScope(reg)) continue;
      try {
        const sub = reg.pushManager ? await reg.pushManager.getSubscription() : null;
        if (sub && sub.options && sub.options.applicationServerKey) found.push(sub);
      } catch (e) { /* 그 등록만 건너뛴다 */ }
    }
    if (!found.length) return 0;
    const { data } = await pushAPI.getVapidKey();
    const key = urlB64ToUint8(data.public_key);
    let dropped = 0;
    for (const sub of found) {
      if (!sameBytes(key, new Uint8Array(sub.options.applicationServerKey))) continue;
      try { if ((await sub.unsubscribe()) !== false) dropped += 1; } catch (e) { /* */ }
    }
    if (dropped) notePushChanged();
    return dropped;
  } catch (e) {
    return 0;
  }
}

// 'granted' | 'denied' | 'default'
export function notificationPermission() {
  try { return (window.Notification && window.Notification.permission) || 'default'; }
  catch (e) { return 'default'; }
}

// 홈 화면/앱으로 설치돼 실행 중인가(설치 안내를 감출 판단)
export function isInstalledApp() {
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    return !!window.navigator.standalone;
  } catch (e) { return false; }
}

// 현재 브라우저를 구독시키고 서버에 등록한다.
// 🔴 권한 요청은 하지 않는다 — 부르는 쪽이 granted 를 보장해야 한다
//    (Notification 권한 묻기는 사용자 제스처 안에서만 유효하다).
export async function subscribeCurrentBrowser() {
  try {
    const reg = await swReady();
    if (!reg) throw new Error('SW_UNAVAILABLE');
    const { data } = await pushAPI.getVapidKey();
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8(data.public_key),
    });
    await pushAPI.subscribe(sub.toJSON());
    return sub;
  } finally {
    notePushChanged();   // 성공·실패 무관(예외는 그대로 위로 올라간다)
  }
}

// 로그아웃 때 이 브라우저의 구독만 끊는다(AppSidebar logout). 끊었으면 true, 끊을 게 없거나 실패면 false.
// 🔴 서버(pushAPI.unsubscribe)는 부르지 않는다 — ① 로그아웃은 토큰을 먼저 지워 이미 401 이고
//    ② 그 엔드포인트는 notifications_enabled 까지 꺼서 재로그인 뒤 자동 재구독(usePushAutoSubscribe)이 막힌다.
//    서버의 옛 구독 행은 다음 발송의 404/410 에서 지워진다.
// 🔴 절대 throw 하지 않는다 — 로그아웃 흐름을 깨면 안 된다.
export async function unsubscribeCurrentBrowser(ms = 3000) {
  try {
    const reg = await swReady(ms);
    if (!reg || !reg.pushManager) return false;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return false;
    return (await sub.unsubscribe()) !== false;
  } catch (e) {
    return false;
  } finally {
    notePushChanged();   // 스스로 삼키므로 throw 하지 않는다
  }
}
